import { useMemo } from "react";
import useIsMobile from "shared/core/hooks/useIsMobile";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { useListHomeCoursesFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { HorizontalScroll } from "shared/UI/HorizontalScroll/HorizontalScroll";
import { DesktopCarousel } from "UIPalette/DesktopCarousel/DesktopCarousel";
import { CourseCard } from "../CourseCard/CourseCard";
import { Container } from "shared/UI/LayoutComponents/LayoutComponents";
import classNames from "classnames";
import Button from "shared/UI/Buttons/Button";
import { useNavigate } from "react-router-dom";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import { useTranslation } from "react-i18next";
import { Breakpoint } from "shared/UI/ResponsiveBreakpoints";

export const WisdomAcademyHomeModule = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "wisdomAcademy" });

  const { mutate, data, isLoading, error } = useListHomeCoursesFunctionRequest({
    xClientCode: ClientInfoState.getCode(),
  });

  const isMobile = useIsMobile(Breakpoint.Desktop);
  const navigate = useNavigate();

  const courseCards = useMemo(
    () =>
      data?.courses.map((course) => (
        <CourseCard
          key={course.id}
          courseData={course}
          className={classNames("min-h-full w-full", {
            "min-w-[300px]": isMobile,
          })}
        />
      )),
    [data, isMobile]
  );

  const hasCourses = courseCards && courseCards.length > 0;

  const header = (
    <Container className={classNames("!h-auto border-t-none lg:border-t border-brand-grey-light !pt-6 !my-0", { "!px-0": !hasCourses })}>
      <div className="flex w-full justify-between items-center">
        <h2>{t("module.heading")}</h2>
        <Button className="max-w-[100px] !w-auto !h-auto" variant="text" text={t("module.headerCta")} onClick={() => navigate("/wisdom-academy")} />
      </div>
    </Container>
  );

  const emptyMessage = <div className="h-full w-full flex justify-center items-center">{t("emptyMessage")}</div>;

  const mobileLayout = (
    <HorizontalScroll className="hide-scrollbar">
      <Container className="!mt-4">
        <div className="flex gap-6">
          {courseCards}
          <div className="min-w-[1px] min-h-full"></div>
        </div>
      </Container>
    </HorizontalScroll>
  );

  const desktopLayout = (
    <Container className="!mt-0">
      <DesktopCarousel>{courseCards}</DesktopCarousel>
    </Container>
  );

  const coursesLayout = isMobile ? mobileLayout : desktopLayout;

  const content = (
    <ViewStateWrapper errorMessage={t("listCoursesErrorMessage")} onRetry={mutate} loading={isLoading} error={!!error}>
      {header}
      {hasCourses ? coursesLayout : emptyMessage}
    </ViewStateWrapper>
  );

  return (
    <div className="h-[500px] border-y lg:border-none border-brand-grey-light lg:mt-10">
      <div
        className={classNames(isLoading || !!error || !hasCourses ? "flex flex-col items-center h-full w-full justify-center" : "left-0 absolute w-screen")}
      >
        {content}
      </div>
    </div>
  );
};
